import moment from 'moment'
import * as React from 'react'
import { RouteChildrenProps } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import {
  FetchDirectRoutesOptions,
  FetchRoutesOptions,
  isAirport,
  PlaceSelectList,
  PlaceType,
} from 'utils/types'
import { FormContainer, SubmitButton, StyledForm, Loader } from './Styles'
import { InputDropdown, DateDropdown } from '../Dropdown'
import { SwitchButton } from '../SwitchButton'
import { FormResponse } from '../FormResponse'
import * as api from 'utils/api'
import { DISPLAY_FORMAT } from 'components/Dropdown/DateDropdown'

const PREVIOUS_AIRPORTS_DESTINATION = 'PREVIOUS_AIRPORTS_DESTINATION'
const PREVIOUS_AIRPORTS_DEPARTURE = 'PREVIOUS_AIRPORTS_DEPARTURE'

const CITIES = [
  'Tokyo',
  'Delhi',
  'Shanghai',
  'Mexico City',
  'Mumbai',
  'New York',
  'Manila',
  'Moscow',
  'Los Angeles',
  'Paris',
  'Minsk',
  'London',
  'Chicago',
  'Madrid',
  'Toronto',
  'Barcelona',
  'Alexandria',
  'Saint Petersburg',
  'Philadelphia',
  'Osaka',
]

const getRandomCities = (): RandomCities => {
  const randomIndex = Math.floor(Math.random() * (CITIES.length - 1))
  return [CITIES[randomIndex], CITIES[randomIndex + (1 % CITIES.length)]]
}

interface Props {
  destination: PlaceSelectList | null
  source: PlaceSelectList | null
  isLoading: boolean
  isMinimized: boolean
  handleSearchRoutes: (options: FetchRoutesOptions) => void
  handleSearchDirectRoutes: (options: FetchDirectRoutesOptions, startPoint: PlaceSelectList) => void
  handleResetDirectRoutes: () => void
  handleEmptyFields: () => void
  handleBrokenLink: () => void
  handleChangeLoading: () => void
  handleClickMinimized: () => void
  handleChangeSource: (value: PlaceSelectList | null) => void
  handleChangeDestination: (value: PlaceSelectList | null) => void
}

type RandomCities = [string, string]

const Form: React.FC<Props & RouteChildrenProps> = (props) => {
  const [source, setSource] = React.useState<PlaceSelectList | null>(null)
  const [destination, setDestination] = React.useState<PlaceSelectList | null>(null)
  const [date, setDate] = React.useState<moment.Moment | null>(null)
  const [byArrival, setByArrival] = React.useState(true)
  const [ogTitleContent, setOgTitleContent] = React.useState<string | undefined>()
  const [titleContent, setTitleContent] = React.useState<string | undefined>()
  const lastRequestValues = React.useRef(Array(4).fill(''))
  const bluredToSubmit = React.useRef(false)
  const [sourcePlaceholder, destinationPlaceholder] = React.useMemo<RandomCities>(
    getRandomCities,
    []
  )

  React.useLayoutEffect(() => {
    setDestination(props.destination)
  }, [props.destination])

  React.useLayoutEffect(() => {
    setSource(props.source)
  }, [props.source])

  React.useEffect(() => {
    if (source && !destination && !date) {
      props.handleSearchDirectRoutes({ source: source.iataToFetch }, source)
      return
    }
    if(destination && !source && !date) {
      props.handleSearchDirectRoutes({ destination: destination.iataToFetch }, destination)
      return
    }
    props.handleResetDirectRoutes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, destination, date])

  const handleChangeSource = React.useCallback((val: PlaceSelectList | null) => {
    setSource(val)
    props.handleChangeSource(val)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeDestination = React.useCallback(
    (val: PlaceSelectList | null) => {
      setDestination(val)
      props.handleChangeDestination(val)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleChangeDate = React.useCallback((val: moment.Moment | null) => setDate(val), [])

  const handleChangeByArrival = React.useCallback((val: boolean) => setByArrival(val), [])

  const handleClickSwitch = () => {
    const oldSource = source ? { ...source } : null
    const oldDestination = destination ? { ...destination } : null
    setSource(oldDestination)
    setDestination(oldSource)
    props.handleChangeDestination(oldSource)
    props.handleChangeSource(oldDestination)
  }

  const handleClickSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    bluredToSubmit.current = false
    if (!date || !source || !destination) {
      props.handleEmptyFields()
      return
    }

    const dateFormatted = date.format('yyyy[-]MM[-]DD')
    const titleDate = moment(date).format("DD MMM 'YY")
    const byArrivalFormatted = byArrival ? 'arriving' : 'departing'
    const requestValues = [source.id, destination.id, dateFormatted, byArrivalFormatted]

    if (lastRequestValues.current.every((value, i) => requestValues[i] === value)) return

    lastRequestValues.current = requestValues

    props.handleSearchRoutes({
      source: source.iataToFetch,
      destination: destination.iataToFetch,
      date: dateFormatted,
      byArrival: byArrival.toString(),
    })
    props.handleChangeLoading()

    setTitleContent(
      `The best flight routes from ${source.iata} to ${destination.iata} ${byArrivalFormatted} ${titleDate} - C Teleport`
    )
    setOgTitleContent(
      `Optimal flights routes - ${source.iata} to ${destination.iata} ${byArrivalFormatted} ${titleDate}`
    )
    props.history.replace(
      `/${source.iata}/${destination.iata}/${byArrivalFormatted}/${dateFormatted}`
    )
  }

  React.useEffect(() => {
    const path = props.location.pathname.replace(/\//, '')
    if (!path.length) return

    const splittedPath = path.split('/')
    const [sourceIata, destinationIata, by_arrival, date] = splittedPath
    const iataReg = /^[a-z]+$/i
    const dateReg = /^\d{4}-\d{2}-\d{2}$/
    const byArrivalReg = /arriving|departing/i
    const isBroken =
      !iataReg.test(sourceIata) ||
      !iataReg.test(destinationIata) ||
      !dateReg.test(date) ||
      !byArrivalReg.test(by_arrival)

    if (isBroken) {
      props.handleBrokenLink()
      return
    }

    Promise.all([api.fetchAirports(sourceIata), api.fetchAirports(destinationIata)])
      .then(([sourcePlaces, destinationPlaces]: PlaceType[][]) => {
        const sourcePlace = Array.isArray(sourcePlaces)
          ? sourcePlaces.find(({ iata }) => iata === sourceIata)
          : null
        const destinationPlace = Array.isArray(destinationPlaces)
          ? destinationPlaces.find(({ iata }) => iata === destinationIata)
          : null
        const formattedByArrival = by_arrival.toLowerCase() === 'arriving' ? true : false
        const formattedDate = moment(date)

        if (!sourcePlace || !destinationPlace || !formattedDate.isValid()) {
          props.handleBrokenLink()
          return
        }

        const sourceRestPlaces = sourcePlaces
          .filter(isAirport)
          .filter(({ city_iata }) => sourcePlace.iata === city_iata)
        const destinationRestPlaces = destinationPlaces
          .filter(isAirport)
          .filter(({ city_iata }) => destinationPlace.iata === city_iata)

        const sourceObj = {
          fullName: `${sourcePlace.iata} ${sourcePlace.name}, ${sourcePlace.country}`,
          name: sourcePlace.name,
          rating: sourcePlace.rating,
          iata: sourcePlace.iata,
          iataToFetch: isAirport(sourcePlace)
            ? sourcePlace.iata
            : sourceRestPlaces.length
            ? sourceRestPlaces.map(({ iata }) => iata)
            : sourcePlace.iata,
          type: sourcePlace.type,
          id: Object.values(sourcePlace.location).join('').replace(/[.-]/g, '') + sourcePlace.iata,
          hasCityWithin: false,
          lat: sourcePlace.location.lat,
          lon: sourcePlace.location.lon,
        }
        const destinationObj = {
          fullName: `${destinationPlace.iata} ${destinationPlace.name}, ${destinationPlace.country}`,
          name: destinationPlace.name,
          rating: destinationPlace.rating,
          iata: destinationPlace.iata,
          iataToFetch: isAirport(destinationPlace)
            ? destinationPlace.iata
            : destinationRestPlaces.length
            ? destinationRestPlaces.map(({ iata }) => iata)
            : destinationPlace.iata,
          type: destinationPlace.type,
          id:
            Object.values(destinationPlace.location).join('').replace(/[.-]/g, '') +
            destinationPlace.iata,
          hasCityWithin: false,
          lat: destinationPlace.location.lat,
          lon: destinationPlace.location.lon,
        }

        setDate(formattedDate)
        setByArrival(formattedByArrival)
        setSource(sourceObj)
        setDestination(destinationObj)

        lastRequestValues.current = [sourceObj.id, destinationObj.id, date, by_arrival]

        props.handleSearchRoutes({
          source: sourceObj.iataToFetch,
          destination: destinationObj.iataToFetch,
          date: date,
          byArrival: formattedByArrival.toString(),
        })
        props.handleChangeLoading()
      })
      .catch((err) => console.info(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <React.Fragment>
      <Helmet>
        {ogTitleContent && <meta property="og:title" content={ogTitleContent} />}
        {titleContent && <title>{titleContent}</title>}
      </Helmet>
      <FormContainer>
        {props.isMinimized && source && destination && date ? (
          <FormResponse
            from={source.name}
            to={destination.name}
            fullDate={date.format(DISPLAY_FORMAT)}
            handleClickEdit={props.handleClickMinimized}
          />
        ) : (
          <StyledForm autoComplete="off">
            <InputDropdown
              label={'From'}
              inputValue={source}
              bluredToSubmit={bluredToSubmit}
              placeholder={`Try "${sourcePlaceholder}"`}
              localStorageKey={PREVIOUS_AIRPORTS_DEPARTURE}
              handleSubmit={handleChangeSource}
            />
            <SwitchButton handleClick={handleClickSwitch} />
            <InputDropdown
              label={'To'}
              inputValue={destination}
              bluredToSubmit={bluredToSubmit}
              placeholder={`Try "${destinationPlaceholder}"`}
              localStorageKey={PREVIOUS_AIRPORTS_DESTINATION}
              handleSubmit={handleChangeDestination}
            />
            <DateDropdown
              byArrival={byArrival}
              inputValue={date}
              placeholder={'Set date'}
              handleChangeDate={handleChangeDate}
              handleChangeByArrival={handleChangeByArrival}
            />
            <SubmitButton
              onClick={handleClickSubmit}
              onMouseDown={() => (bluredToSubmit.current = true)}
            >
              {props.isLoading ? <Loader /> : <p>Search</p>}
            </SubmitButton>
          </StyledForm>
        )}
      </FormContainer>
    </React.Fragment>
  )
}

export default Form
